import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

export type SheetPosition = 'collapsed' | 'partial' | 'expanded' | 'full';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  initialPosition?: SheetPosition;
  fullPageOnMobile?: boolean;
  className?: string;
  hideHeader?: boolean;
}

/**
 * A reusable bottom sheet component that slides up from the bottom of the screen
 * with touch and mouse gesture support for both mobile and desktop devices.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  title,
  initialPosition = 'partial',
  fullPageOnMobile = false,
  className = '',
  hideHeader = false
}) => {
  const [sheetPosition, setSheetPosition] = useState<SheetPosition>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState<number | null>(null);
  const [currentY, setCurrentY] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Reset position when the sheet is opened
  useEffect(() => {
    if (isOpen) {
      setSheetPosition(fullPageOnMobile ? 'full' : initialPosition);
    }
  }, [isOpen, initialPosition, fullPageOnMobile]);
  
  // Add global mouse event listeners when dragging starts
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging && startY !== null) {
        setCurrentY(e.clientY);
        e.preventDefault();
      }
    };
    
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
        document.body.style.userSelect = '';
      }
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, startY]);

  // Shared drag end logic for both touch and mouse
  const handleDragEnd = useCallback(() => {
    if (isDragging && startY !== null && currentY !== null) {
      const diff = currentY - startY;
      
      // Determine if user is swiping up or down
      if (diff > 100) { // Swiping down
        if (sheetPosition === 'full') {
          onClose();
        } else if (sheetPosition === 'expanded') {
          setSheetPosition('partial');
        } else if (sheetPosition === 'partial') {
          setSheetPosition('collapsed');
        } else if (sheetPosition === 'collapsed') {
          onClose();
        }
      } else if (diff < -100) { // Swiping up
        if (sheetPosition === 'collapsed') {
          setSheetPosition('partial');
        } else if (sheetPosition === 'partial') {
          setSheetPosition('expanded');
        } else if (sheetPosition === 'expanded' && fullPageOnMobile) {
          setSheetPosition('full');
        }
      }
    }
    
    setIsDragging(false);
    setStartY(null);
    setCurrentY(null);
  }, [isDragging, startY, currentY, sheetPosition, onClose, fullPageOnMobile]);
  
  // Touch event handlers for mobile devices
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isDragging && startY !== null) {
      setCurrentY(e.touches[0].clientY);
    }
  }, [isDragging, startY]);
  
  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);
  
  // Mouse event handlers for desktop devices
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setStartY(e.clientY);
    document.body.style.userSelect = 'none'; // Prevent text selection during drag
    e.preventDefault();
  }, []);

  const toggleSheetPosition = useCallback(() => {
    if (fullPageOnMobile) {
      if (sheetPosition === 'full') {
        onClose();
      } else {
        setSheetPosition('full');
      }
    } else {
      if (sheetPosition === 'collapsed') {
        setSheetPosition('partial');
      } else if (sheetPosition === 'partial') {
        setSheetPosition('expanded');
      } else {
        setSheetPosition('partial');
      }
    }
  }, [sheetPosition, fullPageOnMobile, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 bg-black bg-opacity-50 md:hidden ${className} transition-opacity duration-300 ease-in-out`}
      onClick={onClose}
    >
      <div 
        ref={sheetRef}
        className={`fixed bg-white dark:bg-gray-900 shadow-lg transition-all duration-300 ease-out transform ${
          sheetPosition === 'full' ? 'inset-0 top-0 rounded-none z-[60]' : 
          'bottom-0 left-0 right-0 rounded-t-xl ' + (
            sheetPosition === 'collapsed' ? 'translate-y-[85%]' : 
            sheetPosition === 'partial' ? 'translate-y-[50%]' : 'translate-y-0'
          )
        }`}
        style={{ 
          height: sheetPosition === 'full' ? '100vh' : '90vh',
          transform: isDragging && currentY !== null && startY !== null ? 
            `translateY(${Math.max(0, currentY - startY)}px)` : undefined
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={(e) => {
          // Only handle mouse down if it's on the drag handle or header
          const target = e.target as HTMLElement;
          const isHandle = target.closest('.drag-handle');
          if (isHandle) {
            handleMouseDown(e);
          }
        }}
      >
        {/* Sheet Header with Handle and Close Button */}
        {!hideHeader && (
        <div className="relative">
          {/* X button always visible in top right with improved visibility */}
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 z-10 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 rounded-full p-2 shadow-md transition-all duration-200 ease-in-out"
            aria-label="Close bottom sheet"
          >
            <X className="w-5 h-5" />
          </button>

          {sheetPosition === 'full' ? (
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-center flex-1">{title}</h3>
            </div>
          ) : (
            <div 
              className="h-10 w-full flex flex-col items-center justify-center cursor-grab active:cursor-grabbing drag-handle"
              onClick={toggleSheetPosition}
              onMouseDown={handleMouseDown}
            >
              {sheetPosition === 'expanded' ? (
                <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              )}
              <div className="w-16 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full mt-1"></div>
            </div>
          )}
        </div>
        )}
        
        {/* Title - only show if not in full page mode since we already show it in the header */}
        {!hideHeader && title && sheetPosition !== 'full' && (
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-center">{title}</h3>
          </div>
        )}
        
        {/* Content */}
        <div className="flex flex-col h-full overflow-hidden">
          {children}
        </div>
        
        {/* Sheet Position Indicator - only show when not in full page mode */}
        {!hideHeader && sheetPosition !== 'full' && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 z-10">
            <div 
              className={`w-2 h-2 rounded-full ${sheetPosition === 'collapsed' ? 'bg-[#ffc11f]' : 'bg-gray-400'}`}
              onClick={() => setSheetPosition('collapsed')}
            ></div>
            <div 
              className={`w-2 h-2 rounded-full ${sheetPosition === 'partial' ? 'bg-[#ffc11f]' : 'bg-gray-400'}`}
              onClick={() => setSheetPosition('partial')}
            ></div>
            <div 
              className={`w-2 h-2 rounded-full ${sheetPosition === 'expanded' ? 'bg-[#ffc11f]' : 'bg-gray-400'}`}
              onClick={() => setSheetPosition('expanded')}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomSheet;
